import {useEffect, useState, useRef, useCallback} from 'react';
import {signInWithCustomToken} from 'firebase/auth';
import {auth} from '@src/firebase';
import {
  useVodacomCheckMutation,
  useVodacomLoginMutation,
  useVodacomRefreshMutation,
  useLazyGetMemberByUsernameQuery,
  useLazyGetUserSubscriptionQuery,
} from '@src/Api/TraceApi/traceApi';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {useCurrentLanguage} from '@src/Store/Slices/LanguageSlice/Language.selector';
import {IDeviceInfo} from '@src/Api/TraceApi/endpoints/sessions.endpoint';
import {SESSION_ID_KEY} from '@src/Providers/SessionProvider/useSessionId';
import {useDispatch} from 'react-redux';
import {isMtnSite} from '@src/Configs/mtnConfig';
import memberSlice, {
  IMemberSubscription,
} from '@src/Store/Slices/Member.slice';
import {
  CARRIER_TO_DISPLAY_PLAN,
  CARRIER_FROM_EXTERNAL_ID,
} from '@src/Hooks/useMemberSubscription';

type VodacomLang = 'pt-BR' | 'fr' | 'en-US';

/**
 * Returns true when the current URL carries Vodacom SSO parameters,
 * meaning the auto-login flow is about to run (or is running).
 * Used by AuthGuardProvider to suspend auth enforcement during SSO.
 */
export function isVodacomSsoInProgress(): boolean {
  if (typeof window === 'undefined') return false;
  if (isMtnSite()) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('e-msisdn') || params.has('msisdn');
}

/**
 * Hook to handle automatic Vodacom SSO login when e-msisdn or msisdn URL parameter is detected.
 *
 * Flow:
 * 1. Detect e-msisdn or msisdn in URL
 * 2. Call /auth/vodacom/check with the parameter
 * 3. If allow_login, call /auth/vodacom/login
 * 4. Authenticate with Firebase using signInWithCustomToken
 * 5. Clean URL parameters after successful authentication
 */
export function useVodacomAutoLogin() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  const [vodacomCheck] = useVodacomCheckMutation();
  const [vodacomLogin] = useVodacomLoginMutation();
  const [vodacomRefresh] = useVodacomRefreshMutation();
  const [fetchMemberByUsernameAndUpdateStore] =
    useLazyGetMemberByUsernameQuery();
  const [getUserSubscription] = useLazyGetUserSubscriptionQuery();
  const dispatch = useDispatch();

  const {setIsLoggedIn, setSessionManuallyInitialized, isLoggedIn} =
    useIsLoggedIn();
  const currentLang = useCurrentLanguage();

  const mapLanguageToVodacom = useCallback(
    (lang: string): VodacomLang => {
      const mapping: Record<string, VodacomLang> = {
        'en-US': 'en-US',
        en: 'en-US',
        fr: 'fr',
        'pt-BR': 'pt-BR',
        pt: 'pt-BR',
      };
      return mapping[lang] || 'en-US';
    },
    [],
  );

  const getDeviceInfo = useCallback((): IDeviceInfo => {
    return {
      name: navigator.userAgent,
      platform: navigator.platform,
      browser: navigator.appName || 'Unknown',
      userAgent: navigator.userAgent,
    };
  }, []);

  const cleanUrlParams = useCallback(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete('e-msisdn');
    url.searchParams.delete('msisdn');

    // Update URL without reload
    window.history.replaceState({}, '', url.toString());
  }, []);

  const refreshSubscriptionAfterReturn = useCallback(async () => {
    try {
      console.log(
        '[Vodacom SSO] User already logged in, refreshing subscription after return from play.trace.plus',
      );
      const response = await vodacomRefresh().unwrap();
      console.log('[Vodacom SSO] Refresh response:', response.action);

      const {data} = await getUserSubscription();
      if (data) {
        const rawProductId = data.externalID;
        const memberSubscription: IMemberSubscription = {
          memberId: data.memberId,
          subscriptionStart: data.subscriptionStart.toString(),
          subscriptionEnd: data.subscriptionEnd?.toString(),
          id: data._id,
          deviceType: data.deviceType,
          subscriptionType: data.subscriptionTypeId,
          productId: CARRIER_TO_DISPLAY_PLAN[rawProductId] || rawProductId,
          cancelRequested: data.cancelRequested,
          carrier: CARRIER_FROM_EXTERNAL_ID[rawProductId],
        };
        dispatch(memberSlice.actions.setMemberSubscription(memberSubscription));
        console.log(
          '[Vodacom SSO] Subscription updated in store:',
          memberSubscription.productId,
        );
      }
    } catch (err) {
      console.warn('[Vodacom SSO] Subscription refresh failed:', err);
    }
  }, [vodacomRefresh, getUserSubscription, dispatch]);

  const processVodacomLogin = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (hasProcessedRef.current) return;
    if (isMtnSite()) return;

    const url = new URL(window.location.href);
    const eMsisdn = url.searchParams.get('e-msisdn');
    const msisdn = url.searchParams.get('msisdn');

    if (!eMsisdn && !msisdn) return;

    // Clean URL params IMMEDIATELY to prevent reuse on page refresh.
    // e-msisdn is a single-use token — Penrose rejects it after the first call.
    hasProcessedRef.current = true;
    cleanUrlParams();

    // If already logged in (normal return from play.trace.plus), just refresh sub
    if (isLoggedIn) {
      await refreshSubscriptionAfterReturn();
      return;
    }

    // Firebase auth restores asynchronously; check if a session already exists
    // before triggering a full login with the (now consumed) e-msisdn.
    const existingUser = auth.currentUser;
    if (!existingUser) {
      const restored = await new Promise<boolean>(resolve => {
        const unsub = auth.onAuthStateChanged(user => {
          unsub();
          resolve(!!user);
        });
        setTimeout(() => resolve(false), 2000);
      });
      if (restored) {
        console.log(
          '[Vodacom SSO] Firebase session restored after refresh — skipping re-login',
        );
        await refreshSubscriptionAfterReturn();
        return;
      }
    } else {
      console.log(
        '[Vodacom SSO] Firebase currentUser exists — skipping re-login',
      );
      await refreshSubscriptionAfterReturn();
      return;
    }

    setIsProcessing(true);
    setError(null);

    console.log('[Vodacom SSO] Starting auto-login flow...', {
      hasEMsisdn: !!eMsisdn,
      hasMsisdn: !!msisdn,
    });

    try {
      setSessionManuallyInitialized(true);

      const checkResponse = await vodacomCheck({
        emsisdn: eMsisdn || undefined,
        msisdn: msisdn || undefined,
      }).unwrap();

      console.log('[Vodacom SSO] Check response:', checkResponse.action);

      if (checkResponse.action === 'redirect_to_penrose') {
        console.log(
          '[Vodacom SSO] Subscription not active, staying on site (no redirect loop)',
        );
        setError('Subscription inactive');
        setSessionManuallyInitialized(false);
        return;
      }

      if (checkResponse.action !== 'allow_login' || !checkResponse.token) {
        console.error('[Vodacom SSO] Unexpected check response', checkResponse);
        setError('Unexpected response from server');
        setSessionManuallyInitialized(false);
        return;
      }

      const loginResponse = await vodacomLogin({
        checkToken: checkResponse.token,
        deviceInfo: getDeviceInfo(),
        lang: mapLanguageToVodacom(currentLang),
      }).unwrap();

      console.log('[Vodacom SSO] Login successful, authenticating with Firebase');

      const userCredential = await signInWithCustomToken(
        auth,
        loginResponse.accessToken,
      );

      if (!userCredential.user) {
        throw new Error('Firebase authentication failed');
      }

      if (loginResponse.session?._id) {
        localStorage.setItem(SESSION_ID_KEY, loginResponse.session._id);
      }

      await fetchMemberByUsernameAndUpdateStore();

      setIsLoggedIn(true);

      console.log('[Vodacom SSO] Auto-login completed successfully');
    } catch (err: any) {
      console.error('[Vodacom SSO] Auto-login failed:', err);
      setError(err?.message || 'Authentication failed');
      setSessionManuallyInitialized(false);

      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error('[Vodacom SSO] Error signing out after failure:', signOutError);
      }
    } finally {
      setIsProcessing(false);
      setSessionManuallyInitialized(false);
    }
  }, [
    isLoggedIn,
    vodacomCheck,
    vodacomLogin,
    fetchMemberByUsernameAndUpdateStore,
    setIsLoggedIn,
    setSessionManuallyInitialized,
    currentLang,
    getDeviceInfo,
    mapLanguageToVodacom,
    cleanUrlParams,
    refreshSubscriptionAfterReturn,
  ]);

  useEffect(() => {
    processVodacomLogin();
  }, [processVodacomLogin]);

  return {
    isProcessing,
    error,
  };
}

export default useVodacomAutoLogin;
